import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { WorkspaceAPI, setActiveWorkspaceId } from '../utils/api';
import { navigateTo } from '../utils/navigation';
import { parseAcceptInviteSearch } from '../utils/acceptInviteUrl';
import {
  rememberAfterLogin,
  rememberInvitePreview,
  peekInvitePreview,
  markJustSignedUp,
  clearJustSignedUp,
  consumeJustSignedUp,
  peekJustSignedUp,
  clearAfterLogin,
} from '../utils/afterLogin';
import { isElectronShell } from '../utils/desktopDownload';
import LogoLoader from '../components/ui/LogoLoader';
import LogoWithTesseract from '../components/ui/LogoWithTesseract';
import MeshGradient from '../components/ui/MeshGradient';
import NoiseTexture from '../components/ui/NoiseTexture';
import DownloadWindowsLink from '../components/ui/DownloadWindowsLink';
import './auth/Auth.css';

function messageForAcceptError(err) {
  const raw = (err?.message || '').toLowerCase();
  if (raw.includes('different email')) {
    return 'different-email';
  }
  if (raw.includes('expired')) {
    return 'This invite has expired. Ask for a new link.';
  }
  if (raw.includes('already a member')) {
    return 'already-member';
  }
  if (raw.includes('not found') || raw.includes('404')) {
    return 'This invite link is no longer valid. It may have been cancelled or already used.';
  }
  if (raw.includes('no email')) {
    return 'This account has no email on it, so we cannot match the invite.';
  }
  return err?.message || 'Could not join this brain.';
}

function invitePath({ workspaceId, inviteId, email, workspaceName }) {
  const params = new URLSearchParams();
  params.set('w', workspaceId);
  params.set('t', inviteId);
  if (email) params.set('e', email);
  if (workspaceName) params.set('n', workspaceName);
  return `/accept-invite?${params.toString()}`;
}

function signedInEmail() {
  const user = window.Auth?.getUser?.();
  return String(user?.email || '').trim().toLowerCase();
}

function emailsMatch(left, right) {
  if (!left || !right) return true;
  return left === right;
}

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const joiningRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [signedAs, setSignedAs] = useState('');

  const parsed = parseAcceptInviteSearch(searchParams);
  const workspaceId = parsed.workspaceId;
  const inviteId = parsed.inviteId;
  const preview = peekInvitePreview() || {};
  const invitedEmail = parsed.email || preview.email || '';
  const brainName = parsed.workspaceName || preview.workspaceName || '';

  const goToRoom = () => {
    clearAfterLogin();
    if (workspaceId) setActiveWorkspaceId(workspaceId);
    navigate('/', { replace: true });
  };

  const storeInvite = () => {
    if (!workspaceId || !inviteId) return;
    rememberAfterLogin(invitePath({ workspaceId, inviteId, email: invitedEmail, workspaceName: brainName }));
    rememberInvitePreview({ email: invitedEmail, workspaceName: brainName });
  };

  // Keep email in session for the locked sign-up form. Drop it from the
  // address bar so a forwarded tab or screenshot is not a mailbox dump.
  const hideEmailInAddressBar = () => {
    if (!workspaceId || !inviteId || !parsed.email) return;
    const next = new URLSearchParams();
    next.set('w', workspaceId);
    next.set('t', inviteId);
    if (brainName) next.set('n', brainName);
    const path = `/accept-invite?${next.toString()}`;
    if (`${window.location.pathname}${window.location.search}` === path) return;
    window.history.replaceState(null, '', path);
  };

  const join = async () => {
    if (!workspaceId || !inviteId || joiningRef.current) return;
    joiningRef.current = true;
    setStatus('joining');
    setMessage('');
    const showWelcome = !isElectronShell() && peekJustSignedUp();
    try {
      await WorkspaceAPI.acceptInvite(workspaceId, inviteId);
      setActiveWorkspaceId(workspaceId);
      clearAfterLogin();
      if (showWelcome) {
        consumeJustSignedUp();
        setStatus('welcome');
        setMessage(brainName ? `You have joined ${brainName}.` : 'You have joined the brain.');
        return;
      }
      consumeJustSignedUp();
      navigate('/', { replace: true });
    } catch (err) {
      const kind = messageForAcceptError(err);
      if (kind === 'already-member') {
        setActiveWorkspaceId(workspaceId);
        clearAfterLogin();
        consumeJustSignedUp();
        if (showWelcome) {
          setStatus('welcome');
          setMessage(brainName ? `You are in ${brainName}.` : 'You are in this brain.');
          return;
        }
        navigate('/', { replace: true });
        return;
      }
      if (kind === 'different-email') {
        setSignedAs(signedInEmail());
        setStatus('wrong-email');
        joiningRef.current = false;
        return;
      }
      setStatus('error');
      setMessage(kind);
      joiningRef.current = false;
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const waitAuth = async () => {
        for (let i = 0; i < 50; i++) {
          if (window.Auth) return window.Auth.isAuthenticated();
          await new Promise((r) => setTimeout(r, 100));
        }
        return false;
      };

      const signedIn = await waitAuth();
      if (!workspaceId || !inviteId) {
        if (!cancelled) {
          setStatus('error');
          setMessage('This invite link is missing details. Ask for a new one.');
        }
        return;
      }
      hideEmailInAddressBar();
      if (!signedIn) {
        storeInvite();
        if (!cancelled) setStatus('choose');
        return;
      }
      const mine = signedInEmail();
      if (!emailsMatch(invitedEmail, mine)) {
        if (!cancelled) {
          setSignedAs(mine);
          setStatus('wrong-email');
        }
        return;
      }
      if (!cancelled) await join();
    })();
    return () => {
      cancelled = true;
    };
    // Open once per link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, inviteId, invitedEmail]);

  const goLogin = () => {
    storeInvite();
    clearJustSignedUp();
    navigateTo('/auth/login');
  };

  const goSignUp = () => {
    storeInvite();
    markJustSignedUp();
    navigateTo('/auth/register');
  };

  const useInvitedEmail = async () => {
    storeInvite();
    if (window.Auth?.logout) await window.Auth.logout();
  };

  const namedBrain = brainName || 'this brain';

  return (
    <div className="auth-container">
      <MeshGradient />
      <NoiseTexture />
      <div className="auth-card">
        {status === 'loading' && (
          <div className="auth-header">
            <LogoLoader size={72} />
            <p className="auth-tagline" style={{ marginTop: 20 }}>
              Opening invite…
            </p>
          </div>
        )}

        {status === 'choose' && (
          <div className="auth-header auth-header--invite">
            <div className="auth-invite-logo">
              <LogoWithTesseract
                logoSrc={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`}
                logoAlt="Fokus"
                size={120}
              />
            </div>
            <h1 className="auth-title">
              You&apos;ve been invited to join {namedBrain} on Fokus
            </h1>
            <p className="auth-tagline">
              Create an account and you&apos;re in.
            </p>
            <div className="auth-invite-actions">
              <button
                type="button"
                className="auth-button auth-button--primary"
                onClick={goSignUp}
              >
                Create a free account to join
              </button>
            </div>
            <p className="auth-invite-login">
              Already have an account?{' '}
              <button
                type="button"
                className="auth-link-button"
                onClick={goLogin}
              >
                Log in
              </button>
            </p>
          </div>
        )}

        {status === 'joining' && (
          <div className="auth-header">
            <LogoLoader size={72} />
            <p className="auth-tagline" style={{ marginTop: 20 }}>
              Joining {namedBrain}…
            </p>
          </div>
        )}

        {status === 'welcome' && (
          <div className="auth-header">
            <h1 className="auth-title">You are in</h1>
            <p className="auth-tagline">{message}</p>
            <div className="auth-invite-actions">
              <DownloadWindowsLink variant="primary" />
              <button
                type="button"
                className="auth-button auth-button--social"
                onClick={goToRoom}
              >
                Open brain
              </button>
            </div>
          </div>
        )}

        {status === 'wrong-email' && (
          <div className="auth-header">
            <h1 className="auth-title">This invite is for someone else</h1>
            <p className="auth-tagline">
              {invitedEmail
                ? `This invite is for ${invitedEmail}. You are signed in as ${signedAs || 'a different address'}.`
                : `You are signed in as ${signedAs || 'a different address'}. Sign in with the email that was invited.`}
            </p>
            <div className="auth-invite-actions">
              <button
                type="button"
                className="auth-button auth-button--primary"
                onClick={useInvitedEmail}
              >
                Use the invited email
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="auth-header">
            <h1 className="auth-title">Invite</h1>
            <p className="auth-tagline">{message}</p>
            <button
              type="button"
              className="auth-button auth-button--primary"
              style={{ marginTop: 24 }}
              onClick={() => navigate('/', { replace: true })}
            >
              Back to Fokus
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
